import {
  berthHomePort,
  homePortPoint,
  homePortStop,
  starredHomePort,
  type StarredMarina,
  stopPoint,
} from '../store/vessel-marinas/home-port.ts';
import type { Berth } from '../store/vessel-marinas/vessel-marinas.dto.ts';
import type { HomePort } from '../store/vessels/vessel.dto.ts';

import { formatEventDateRange } from './formatEventDateRange.ts';
import {
  distanceHint,
  metersBetween,
  rankByDistance,
  type DistanceFrom,
  type HintPart,
} from './placeDistance.ts';

/**
 * The location picker's own places, the vessel's berths and starred marinas and the catalog,
 * made into the same rows as Google's and merged with them.
 *
 * `.ts` imports: `node --test` runs this file and cannot resolve `@/`.
 */

/** A row the picker offers, and the facts on the line under it. */
export type Suggestion = {
  value: string;
  label: string;
  distanceMeters?: number;
  parts: HintPart[];
};

type Point = { latitude: number; longitude: number };
type Source = 'saved' | 'catalog';

const SOURCE: Record<Source, HintPart> = {
  saved: { kind: 'source', text: 'From My Marinas' },
  catalog: { kind: 'source', text: 'From catalog' },
};

const BERTH_TYPE: Record<Berth['type'], string> = {
  permanent: 'Permanent berth',
  temporary: 'Temporary berth',
};

/** A saved port the trip map has nothing to place by: said up front, so nobody expects a pin. */
export const noMapHint = (port: HomePort): HintPart[] =>
  stopPoint(port) === null ? [{ kind: 'nomap', text: 'No location to chart' }] : [];

/** Stops are names a trip map geocodes later, so the value is the home port's "name, city". */
const suggestion = (
  port: HomePort,
  origin: Point | undefined,
  from: DistanceFrom,
  lead: (HintPart | undefined)[],
  source: Source,
): Suggestion => {
  const point = homePortPoint(port);
  const distanceMeters = origin && point ? metersBetween(origin, point) : undefined;
  return {
    value: homePortStop(port),
    label: port.name,
    distanceMeters,
    parts: [
      ...lead,
      // Only saved ones: the trip map knows My Marinas, and geocodes a catalog marina's name.
      ...(source === 'saved' ? noMapHint(port) : []),
      port.city ? { kind: 'city', text: port.city } : undefined,
      distanceHint(distanceMeters, from),
      SOURCE[source],
    ].filter((part): part is HintPart => !!part),
  };
};

export const berthSuggestion = (berth: Berth, origin: Point | undefined, from: DistanceFrom) =>
  suggestion(
    berthHomePort(berth),
    origin,
    from,
    [
      { kind: 'type', text: BERTH_TYPE[berth.type] },
      berth.dateFrom && berth.dateTo
        ? { kind: 'dates', text: formatEventDateRange(berth.dateFrom, berth.dateTo) }
        : undefined,
    ],
    'saved',
  );

/** A catalog marina: `saved` when the vessel starred it, which puts it in My Marinas. */
export const marinaSuggestion = (
  marina: StarredMarina,
  origin: Point | undefined,
  from: DistanceFrom,
  source: Source,
) => suggestion(starredHomePort(marina), origin, from, [], source);

export const matchesQuery = (place: Suggestion, query: string) =>
  place.value.toLowerCase().includes(query.trim().toLowerCase());

// ponytail: a Google place sharing a name with a saved or catalog one in another town is
// dropped too. Comparing coordinates is the upgrade, once Autocomplete returns them.
/** The first row of each name wins; the name is the text before the first comma. */
export const uniqueByName = <T extends { value: string }>(places: T[]) => {
  const seen = new Set<string>();
  return places.filter(({ value }) => {
    const name = value.split(',')[0].trim().toLowerCase();
    if (seen.has(name)) return false;
    seen.add(name);
    return true;
  });
};

/** One list by distance tier; inside a tier, and on a shared name, saved beats catalog beats Google. */
export const mergeSuggestions = (
  saved: Suggestion[],
  catalog: Suggestion[],
  google: Suggestion[],
) => rankByDistance(uniqueByName([...saved, ...catalog, ...google]));
